import { ChangeDetectorRef, Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DxToastModule, DxTreeListModule } from 'devextreme-angular';
import { DxTreeListComponent, DxTreeListTypes } from 'devextreme-angular/ui/tree-list';
import { ConfigService } from 'src/app/services/config.service';
import { GpdButtonComponent } from 'src/app/components/gpd-button/gpd-button.component';
import { AppGlobals } from 'src/app/app.globals';

import { ActivityLogService } from 'src/app/services/activity-log.service';
import { ActivitySection } from 'src/app/constants/activity-log';

// Admin settings screen (Feature #4483 / Story #4527 + #4528): lets an admin select which
// tasks from the Phase/Milestone/TaskPackage/Task hierarchy feed the "Daten" section of the
// project overview page. Built as a dx-tree-list (matching the Figma design's bordered table
// with a dedicated checkbox column + indented hierarchy column, chevron-down expand icons),
// not a dx-tree-view — the built-in search panel covers #4528's search/auto-expand requirement.
@Component({
    selector: 'app-overview-task-config',
    imports: [CommonModule, DxTreeListModule, DxToastModule, GpdButtonComponent],
    templateUrl: './overview-task-config.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './overview-task-config.component.css'
})
export class OverviewTaskConfigComponent implements OnInit {
  @ViewChild(DxTreeListComponent, { static: false }) treeList: DxTreeListComponent;

  overviewTasks: any[] = [];
  selectedTaskIds: number[] = [];

  toastVisible = false;

  constructor(
    private configService: ConfigService,
    private activityLog: ActivityLogService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.activityLog.logView({ Section: ActivitySection.CONFIG_PROJECT_OVERVIEW_TASKS });
    this.getPageData();
  }

  getPageData() {
    this.configService.getProjectOverviewConfig({}).then((result) => {
      this.overviewTasks = result.projectOverviewTaskItems;
      this.selectedTaskIds = result.selectedTaskIds ?? [];
      // The routed parent (ProjectConfigComponent) uses ChangeDetectionStrategy.OnPush.
      // Since this async response resolves inside this child's own callback (not an
      // event that originated in the parent's template), nothing marks that OnPush
      // ancestor dirty, so Angular's change-detection tree-walk skips right past it and
      // never re-checks this component's [dataSource] binding - the dx-tree-list widget
      // keeps its initial empty dataSource even though overviewTasks is now populated.
      // markForCheck() flags this view and every ancestor up to the root as needing a
      // check, which is exactly what's needed to get past that OnPush boundary.
      this.cdr.markForCheck();
    });
  }

  private initialSelectionApplied = false;

  onContentReady(e: DxTreeListTypes.ContentReadyEvent) {
    // Apply the saved selection once, on first render *with data*. The widget renders (and
    // fires this) once before getPageData() resolves, while overviewTasks is still the empty
    // array the field is initialised to - claiming that render as "the first one" burned the
    // flag on an empty tree with an empty selectedTaskIds, so the saved selection that arrived
    // moments later was never pushed into the widget and every checkbox stayed unchecked.
    if (this.initialSelectionApplied || !this.overviewTasks.length) return;
    // onContentReady also fires on every search keystroke and expand/collapse, and re-running
    // selectRows there would push the selection back through the currently filtered view.
    this.initialSelectionApplied = true;
    e.component.selectRows(this.selectedTaskIds, false);
  }

  onSelectionChanged(e: DxTreeListTypes.SelectionChangedEvent) {
    // 'all' is the whole selection, and the only documented thing that narrows it is remote
    // operations - this dataSource is a plain in-memory array (see the [dataSource] binding),
    // so collapsing a branch or filtering with #4528's search panel does not hide keys from it.
    // Two earlier attempts to compensate for a narrowing that never happens were the bug:
    //   - 'leavesOnly' drops a checked task that itself has subtasks, and ConfigLogic's
    //     ParentTaskId fallback means a real task can parent another real task, so interior
    //     nodes are not only the synthetic group rows.
    //   - re-adding ids missing from getVisibleRows() ("currently rendered rows", so collapsed
    //     and filtered-out rows are absent) made deselection impossible: unchecking a collapsed
    //     group put every descendant straight back, and Save wrote the old set under a success
    //     toast. Harmless while the selection was always empty; live the moment #4710 was fixed.
    // Phase/Milestone/TaskPackage rows carry synthetic negative ids that match no task, so the
    // one filter that is actually needed is dropping those.
    this.selectedTaskIds = (e.component.getSelectedRowKeys('all') as number[]).filter(id => id > 0);
  }

  save() {
    this.configService.saveProjectOverviewConfig({ SelectedTaskIds: this.selectedTaskIds }).then((result) => {
      if (result.hasError) {
        AppGlobals.ShowErrorMessage(result.feedback);
      } else {
        this.toastVisible = true;
      }
    });
  }
}
